import React from "react";
import { apiBayar, formatApiErrorDetail } from "@/lib/api";
import { Loader2, Wallet, Printer } from "lucide-react";

import { rupiah } from "@/lib/format";
import { useCetak, usePengaturan } from "@/hooks/usePengaturan";
import { useKunciKirim } from "@/hooks/useKunciKirim";

export { rupiah };
const inputCls = "mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60";
const labelCls = "text-[13px] font-semibold text-slate-200";

// Records one payment. The server books at most the balance, computes the
// change and the new status; this form only collects what was handed over.
export default function BayarForm({ notaId, ringkasan, rekening, onPaid, testid = "bayar" }) {
  const kirim = useKunciKirim(); // paket 1: a retry of the same payment is booked once
  const cetak = useCetak();
  const cfg = usePengaturan();
  const [metode, setMetode] = React.useState("TRANSFER");
  const [tunai, setTunai] = React.useState("");
  const [nonTunai, setNonTunai] = React.useState("");
  const [rekeningId, setRekeningId] = React.useState("");
  const [keterangan, setKeterangan] = React.useState("");
  const [lebih, setLebih] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [hasil, setHasil] = React.useState(null);
  const aktif = (rekening || []).filter((r) => r.is_active);
  // Addendum 5 (A5-F): QRIS only once the owner chose the QRIS account; the server books it there
  const rekQris = cfg?.qris_rekening_id ? (cfg.rekening || []).find((r) => r.id === cfg.qris_rekening_id && r.is_active !== false) : null;
  const qris = metode === "QRIS" && !!rekQris;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const r = await apiBayar(notaId, {
        tunai_diterima: parseInt(tunai, 10) || 0,
        non_tunai: parseInt(nonTunai, 10) || 0,
        rekening_id: qris ? null : rekeningId || null,
        metode: qris ? "QRIS" : "TRANSFER",
        keterangan: keterangan || null,
        kelebihan_transfer: qris ? false : lebih,
      }, kirim.ambil());
      kirim.selesai();
      setHasil(r); setTunai(""); setNonTunai(""); setKeterangan(""); setLebih(false);
      onPaid && onPaid(r);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={testid} className="space-y-3">
      {hasil && (
        <div data-testid={`${testid}-hasil`} className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 flex flex-wrap items-center gap-4">
          <div>
            <div className="text-xs text-emerald-300 font-mono uppercase">Pembayaran tercatat · {hasil.ringkasan.status_bayar.replace("_", " ")}</div>
            <div className="text-sm text-slate-200">Dibukukan {rupiah(hasil.pembayaran.jumlah)} · Sisa {rupiah(hasil.ringkasan.sisa)}</div>
            {hasil.pembayaran.kelebihan > 0 && <div data-testid={`${testid}-kelebihan`} className="text-sm text-amber-300">Kelebihan transfer {rupiah(hasil.pembayaran.kelebihan)} dicatat — Finance mengembalikan paling lambat 2×24 jam (biaya admin ditanggung customer).</div>}
          </div>
          <div className="ml-auto text-right">
            <div className="text-xs text-slate-400">Kembalian</div>
            <div data-testid={`${testid}-kembalian`} className="text-2xl font-bold text-amber-300">{rupiah(hasil.pembayaran.kembalian)}</div>
          </div>
          {cetak.nota && <a data-testid={`${testid}-cetak`} href={`/nota/${notaId}/cetak?pembayaran=${hasil.pembayaran.id}`} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-600 px-3 py-1.5 text-xs text-slate-200 hover:border-amber-500/50"><Printer size={13} /> Cetak nota</a>}
        </div>
      )}
      {ringkasan && ringkasan.sisa > 0 && (
        <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <div><label className={labelCls}>Tunai diterima</label>
            <input data-testid={`${testid}-tunai`} type="number" min="0" value={tunai} onChange={(e) => setTunai(e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Non tunai</label>
            <input data-testid={`${testid}-non-tunai`} type="number" min="0" value={nonTunai} onChange={(e) => setNonTunai(e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>{rekQris ? "Non tunai lewat" : "Rekening tujuan"}</label>
            {rekQris && (
              <select data-testid={`${testid}-metode`} value={metode} onChange={(e) => setMetode(e.target.value)} className={inputCls}>
                <option value="TRANSFER">Transfer</option><option value="QRIS">QRIS</option>
              </select>)}
            {qris
              ? <div data-testid={`${testid}-rek-qris`} className="mt-1 text-xs text-slate-300">Dicatat ke rekening QRIS {rekQris.bank} {rekQris.nomor}. QRIS statis: catat hanya setelah dana terlihat masuk di aplikasi bank, bukan dari screenshot customer.</div>
              : <select data-testid={`${testid}-rekening`} value={rekeningId} onChange={(e) => setRekeningId(e.target.value)} disabled={!(parseInt(nonTunai, 10) > 0)} className={`${inputCls} disabled:opacity-40`}>
                  <option value="">— pilih rekening —</option>
                  {aktif.map((r) => <option key={r.id} value={r.id}>{r.bank} {r.nomor} a.n. {r.atas_nama}</option>)}
                </select>}</div>
          <div><label className={labelCls}>Keterangan</label>
            <input data-testid={`${testid}-keterangan`} value={keterangan} onChange={(e) => setKeterangan(e.target.value)} placeholder="mis. DP" className={inputCls} /></div>
          <button data-testid={`${testid}-submit`} type="submit" disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-50">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Wallet size={16} />} Bayar
          </button>
          {!qris && parseInt(nonTunai, 10) > ringkasan.sisa && (
            <label className="col-span-2 md:col-span-5 inline-flex items-center gap-2 text-sm text-amber-200">
              <input data-testid={`${testid}-kelebihan-ok`} type="checkbox" checked={lebih} onChange={(e) => setLebih(e.target.checked)} />
              Customer memang mentransfer {rupiah(parseInt(nonTunai, 10))} (lebih {rupiah(parseInt(nonTunai, 10) - ringkasan.sisa)}). Kelebihan dicatat untuk dikembalikan.
            </label>
          )}
          <p className="col-span-2 md:col-span-5 text-xs text-slate-500">Sisa tagihan {rupiah(ringkasan.sisa)}. Tunai lebih dari sisa dicatat sebagai kembalian. Transfer yang lebih: isi jumlah yang benar-benar masuk lalu centang konfirmasi kelebihan.</p>
        </form>
      )}
      {error && <div data-testid={`${testid}-error`} className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</div>}
    </div>
  );
}
